/* External */
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { List, Set as ImmutableSet } from 'immutable';

/* Ott-common */
import { EntityName } from '@dtvgo/backend-service/normalize';

/* Components */
import LanderLayout from '../LanderLayout';
import VirtualizedList, { ItemDataProvider } from '../VirtualizedList';
import LanderRow from './LanderRow';
import LanderSkeleton from '../LanderSkeleton';

/* Utils */
import { useLoading } from '../../providers/LoadingProvider';
import useFetchPage from '../../hooks/useFetchPage';
import useResetFocus from '../../hooks/useResetFocus';
import {
  PageType,
  SOURCE_CAROUSEL_ID,
  STARTED_PAGE_ID,
} from '../../utils/constants';
import {
  getCardType,
  getCarouselHeight,
  getInitialCardType,
  sizeByViewport,
  CardType,
  BannerType,
} from '../../utils/cardsUtils';
import { useOpenSidebar } from '../../providers/SidebarStateContext';
import { getIsCurableBannerActive } from '../../utils/functions';
import { pickCarouselInfoList } from '../../utils/pageCarouselList';
import {
  isLanderBannerActive,
  isRowAdsBannerActive,
  isToastBannerActive,
} from '../../utils/env';

/* Hooks */
import useGetUpSellBannerMessage from '../../hooks/useGetUpSellBannerMessage';

/* Styles */
import './LanderPage.scss';
import usePageCarousels from '../../hooks/usePageCarousels';
import { mapHomeBannerPayload } from '../HomeBannerSlot/mapHomeBannerPayload';
import { getCommunicationCarousel } from '../../services/communicationCarousel';
import {
  isToastBannerRowType,
  getCarouselRowBannerId,
  readCarouselField,
  readCarouselRowType,
  isHomeBannerRowType,
  isCommunicationBannerFetchEnabledForRowType,
} from '../../utils/landerCommunicationHelpers';

const landerPageHeight = sizeByViewport(1080);

/**
 * When a toast-banner exists, moves it to index 0 of the filtered lander list (before TA_HERO,
 * home-banner, and any other filtered row). If there is no toast, order is unchanged.
 */
function promoteFirstToastBannerToTop(carouselList) {
  if (!carouselList || carouselList.size === 0) return carouselList;

  let firstToastIndex = -1;
  for (let i = 0; i < carouselList.size; i += 1) {
    const item = carouselList.get(i);
    if (isToastBannerRowType(readCarouselRowType(item))) {
      firstToastIndex = i;
      break;
    }
  }

  if (firstToastIndex === -1 || firstToastIndex === 0) {
    return carouselList;
  }

  const toastCarousel = carouselList.get(firstToastIndex);
  return carouselList.splice(firstToastIndex, 1).splice(0, 0, toastCarousel);
}

/**
 * @typedef {object} LanderCommStackCtx
 * @property {import('immutable').List} carouselsToShow
 * @property {string} firstToastBannerCarouselId
 * @property {boolean} isToastBannerActive
 * @property {boolean} isRowAdsBannerActive
 * @property {boolean} isLanderBannerActive
 */

function resolveToastRowStackCardType(index, ctx) {
  const { carouselsToShow, firstToastBannerCarouselId, isLanderBannerActive: landerActive } = ctx;
  const carouselInfo = carouselsToShow.get(index);
  const toastId = getCarouselRowBannerId(carouselInfo);
  if (String(toastId) !== String(firstToastBannerCarouselId ?? '')) {
    return CardType.TOAST_COMMUNICATION_BANNER;
  }
  const nextCarousel = carouselsToShow.get(index + 1);
  if (isHomeBannerRowType(readCarouselRowType(nextCarousel))) {
    return landerActive
      ? CardType.HOME_COMMUNICATION_BANNER
      : CardType.TOAST_COMMUNICATION_BANNER;
  }
  const prevCarousel = carouselsToShow.get(index - 1);
  if (isHomeBannerRowType(readCarouselRowType(prevCarousel))) {
    return CardType.EMPTY;
  }
  return CardType.TOAST_COMMUNICATION_BANNER;
}

function resolveHomeRowStackCardType(index, ctx) {
  const {
    carouselsToShow,
    firstToastBannerCarouselId,
    isToastBannerActive: toastFlag,
    isRowAdsBannerActive: rowAdsFlag,
  } = ctx;
  const prevCarousel = carouselsToShow.get(index - 1);
  const prevRowType = readCarouselRowType(prevCarousel);
  const prevFetchOn = isCommunicationBannerFetchEnabledForRowType(
    prevRowType,
    toastFlag,
    rowAdsFlag,
  );
  if (
    prevFetchOn
    && getCarouselRowBannerId(prevCarousel) === firstToastBannerCarouselId
  ) {
    return CardType.EMPTY;
  }
  const nextCarousel = carouselsToShow.get(index + 1);
  if (
    isToastBannerRowType(readCarouselRowType(nextCarousel))
    && getCarouselRowBannerId(nextCarousel) === firstToastBannerCarouselId
  ) {
    return CardType.HOME_COMMUNICATION_BANNER;
  }
  return CardType.HOME_COMMUNICATION_BANNER_COMPACT;
}

function hasEarlierDuplicateToastCarousel(index, carouselsToShow) {
  const carouselInfo = carouselsToShow?.get(index);
  if (!carouselInfo || !isToastBannerRowType(readCarouselRowType(carouselInfo))) {
    return false;
  }
  const normThis = String(getCarouselRowBannerId(carouselInfo) ?? '').trim();
  if (!normThis) return false;
  for (let i = 0; i < index; i += 1) {
    const c = carouselsToShow.get(i);
    if (!isToastBannerRowType(readCarouselRowType(c))) continue;
    if (String(getCarouselRowBannerId(c) ?? '').trim() === normThis) return true;
  }
  return false;
}

function resolveStackCardTypeForIndex(index, ctx) {
  const carouselInfo = ctx.carouselsToShow?.get(index);
  if (!carouselInfo) return undefined;

  if (isToastBannerRowType(readCarouselRowType(carouselInfo))) {
    if (hasEarlierDuplicateToastCarousel(index, ctx.carouselsToShow)) {
      return CardType.EMPTY;
    }
    return resolveToastRowStackCardType(index, ctx);
  }

  if (isHomeBannerRowType(readCarouselRowType(carouselInfo))) {
    return resolveHomeRowStackCardType(index, ctx);
  }

  return undefined;
}

function isCommunicationCarouselIdCollapsed(collapsedIds, rawId) {
  const s = String(rawId ?? '').trim();
  return Boolean(s && collapsedIds?.has(s));
}

function adjustCollapsedToastOnlyRow(stackCardType, carouselInfo, collapsedIds) {
  if (stackCardType !== CardType.TOAST_COMMUNICATION_BANNER) return undefined;
  return isCommunicationCarouselIdCollapsed(collapsedIds, getCarouselRowBannerId(carouselInfo))
    ? CardType.EMPTY
    : stackCardType;
}

function adjustCollapsedToastHomeStackRow({
  stackCardType,
  carouselInfo,
  nextCarousel,
  collapsedIds,
}) {
  if (stackCardType !== CardType.HOME_COMMUNICATION_BANNER) return undefined;
  const toastId = getCarouselRowBannerId(carouselInfo);
  const homeId = isHomeBannerRowType(readCarouselRowType(nextCarousel))
    ? getCarouselRowBannerId(nextCarousel)
    : '';
  const toastGone = isCommunicationCarouselIdCollapsed(collapsedIds, toastId);
  const homeGone = Boolean(homeId) && isCommunicationCarouselIdCollapsed(collapsedIds, homeId);
  if (toastGone && homeGone) return CardType.EMPTY;
  if (toastGone) return CardType.HOME_COMMUNICATION_BANNER_COMPACT;
  if (homeGone) return CardType.TOAST_COMMUNICATION_BANNER;
  return stackCardType;
}

function adjustCollapsedHomeCompactRow(stackCardType, carouselInfo, collapsedIds) {
  if (stackCardType !== CardType.HOME_COMMUNICATION_BANNER_COMPACT) return undefined;
  return isCommunicationCarouselIdCollapsed(collapsedIds, getCarouselRowBannerId(carouselInfo))
    ? CardType.EMPTY
    : stackCardType;
}

function adjustCollapsedToastFeaturedStackRow({
  stackCardType,
  carouselInfo,
  nextCarousel,
  collapsedIds,
  noFeatured,
}) {
  if (stackCardType !== CardType.TOAST_FEATURED_COMMUNICATION_STACK) return undefined;
  return isCommunicationCarouselIdCollapsed(collapsedIds, getCarouselRowBannerId(carouselInfo))
    ? getInitialCardType(nextCarousel, noFeatured)
    : stackCardType;
}

/**
 * Recalcula o CardType quando o fetch de comunicação falha ou retorna vazio,
 * para não reservar altura de lista virtualizada em branco.
 */
function adjustStackCardTypeForCollapsedCommunication({
  index,
  stackCardType,
  carouselsToShow,
  collapsedIds,
  noFeatured,
}) {
  if (
    stackCardType === undefined
    || stackCardType === CardType.EMPTY
    || !collapsedIds?.size
  ) {
    return stackCardType;
  }

  const carouselInfo = carouselsToShow?.get(index);
  const nextCarousel = carouselsToShow?.get(index + 1);

  return (
    adjustCollapsedToastOnlyRow(stackCardType, carouselInfo, collapsedIds)
    ?? adjustCollapsedToastHomeStackRow({
      stackCardType,
      carouselInfo,
      nextCarousel,
      collapsedIds,
    })
    ?? adjustCollapsedHomeCompactRow(stackCardType, carouselInfo, collapsedIds)
    ?? adjustCollapsedToastFeaturedStackRow({
      stackCardType,
      carouselInfo,
      nextCarousel,
      collapsedIds,
      noFeatured,
    })
    ?? stackCardType
  );
}

function cardTypeWhenCommunicationRowDisabledByFlag(carouselInfo, commFlags) {
  if (!carouselInfo) return undefined;
  const { toastActive, landerActive, rowAdsActive } = commFlags;
  const rowType = readCarouselRowType(carouselInfo);
  if (
    isToastBannerRowType(rowType)
    && !isCommunicationBannerFetchEnabledForRowType(rowType, toastActive, rowAdsActive)
  ) {
    return CardType.EMPTY;
  }
  if (isHomeBannerRowType(rowType) && !landerActive) {
    return CardType.EMPTY;
  }
  return undefined;
}

function LanderPage({
  className = '',
  style: styleProp = undefined,
  pageId,
  sourceProvider = undefined,
  children = undefined,
  preContent = undefined,
  onScroll = undefined,
  initialMaxOffset = 0,
  withoutFeatured = false,
  onFetchRecentlyWatched = undefined,
  withoutTitle = false,
  setIsPageFetching = undefined,
  isUsingSkeleton = true,
  carouselFilter = (carouselInfo) => !!carouselInfo,
  showItems = true,
  onTopCarouselFocusKeyChange = undefined,
}) {
  const [scrolled, setScrolled] = useState(false);
  const [position, setPosition] = useState(false);
  const [fetchedCarouselsIds, setFetchedCarouselsIds] = useState(() => ImmutableSet());
  const [fetchedCarousels, setFetchedCarousels] = useState(() => List());
  const [wasCarouselFocused, setWasCarouselFocused] = useState(false);
  const [collapsedCommunicationIds, setCollapsedCommunicationIds] = useState(
    () => new Set(),
  );
  const [initialScrollPosition] = useState(() => Number.parseFloat(sessionStorage.getItem('offsetFromStartedPage') || 0));
  const { hideLoading } = useLoading();
  const goToSidebar = useOpenSidebar();
  const setAllSessionStorage = useResetFocus(pageId);
  const [initialSectionIndexEpg] = useState(() => Number.parseFloat(sessionStorage.getItem('initialSectionIndexEpg') || 0));
  const [parentalBlockedChannelId] = useState(() => sessionStorage.getItem('parentalBlockedChannelId') || '');
  const isCurableBannerActive = getIsCurableBannerActive();
  const { showUpSellBannerCarousel } = useGetUpSellBannerMessage(isCurableBannerActive) || {};

  const isHomeLander = pageId === PageType.HOME;
  const toastActiveForCommunication = isHomeLander && isToastBannerActive;
  const rowAdsActiveForCommunication = isHomeLander && isRowAdsBannerActive;
  const landerBannerActiveForCommunication = isHomeLander && isLanderBannerActive;

  const registerCommunicationUnavailable = useCallback((rawCarouselId) => {
    const id = String(rawCarouselId ?? '').trim();
    if (!id) return;
    setCollapsedCommunicationIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const positionsList = useRef();
  const getPositionsList = useCallback(() => {
    if (!positionsList.current) {
      positionsList.current = [];
    }
    return positionsList.current;
  }, []);
  const translateValuesList = useRef();
  const getTranslateValuesList = useCallback(() => {
    if (!translateValuesList.current) {
      translateValuesList.current = [];
    }
    return translateValuesList.current;
  }, []);

  const { page, isFetching } = useFetchPage(pageId, sourceProvider);

  /** Igual a `usePageCarousels`: `pickCarouselInfoList` (elements + toast). */
  const carouselsInfo = useMemo(() => {
    if (!page) return undefined;
    const chosen = pickCarouselInfoList(page);
    if (chosen === undefined || chosen === null) return undefined;
    if (List.isList(chosen)) return chosen;
    if (Array.isArray(chosen)) return List(chosen);
    return chosen;
  }, [page]);

  const carouselsFromStore = usePageCarousels(page);

  // It should be removed from the code when pages v4 is the final version.
  const carousels = useMemo(() => {
    if (isCurableBannerActive || !showUpSellBannerCarousel) return carouselsInfo;

    return carouselsInfo?.insert(1, { type: BannerType.UPSELL_BANNER });
  }, [carouselsInfo, showUpSellBannerCarousel, isCurableBannerActive]);

  const carouselsToShow = useMemo(
    () => {
      if (!carousels) return carousels;

      const filtered = carousels.filter(carouselFilter);
      return promoteFirstToastBannerToTop(filtered);
    },
    [carouselFilter, carousels],
  );

  const firstToastBannerCarouselId = useMemo(() => {
    if (!carouselsToShow?.size) return '';
    for (let i = 0; i < carouselsToShow.size; i += 1) {
      const c = carouselsToShow.get(i);
      if (isToastBannerRowType(readCarouselRowType(c))) {
        return getCarouselRowBannerId(c);
      }
    }
    return '';
  }, [carouselsToShow]);

  /** Toast/dialog + home-banner (prefetch único; sem esperar montagem dos slots). */
  const communicationCarouselIdsForPrefetch = useMemo(() => {
    if (!carouselsToShow?.size) return [];
    const seen = new Set();
    const ordered = [];
    for (let i = 0; i < carouselsToShow.size; i += 1) {
      const c = carouselsToShow.get(i);
      const rowType = readCarouselRowType(c);
      let eligible = false;
      if (isToastBannerRowType(rowType)) {
        eligible = isCommunicationBannerFetchEnabledForRowType(
          rowType,
          toastActiveForCommunication,
          rowAdsActiveForCommunication,
        );
      } else if (isHomeBannerRowType(rowType) && landerBannerActiveForCommunication) {
        eligible = true;
      }
      if (!eligible) continue;
      const bid = getCarouselRowBannerId(c);
      if (bid && !seen.has(bid)) {
        seen.add(bid);
        ordered.push(bid);
      }
    }
    return ordered;
  }, [
    carouselsToShow,
    toastActiveForCommunication,
    rowAdsActiveForCommunication,
    landerBannerActiveForCommunication,
  ]);

  const toastPrefetchKey = communicationCarouselIdsForPrefetch.join('|');

  const [communicationPrefetch, setCommunicationPrefetch] = useState(() => ({
    key: '',
    availableIds: new Set(),
  }));

  const unavailableCommunicationIds = useMemo(() => {
    const unavailable = new Set(collapsedCommunicationIds);
    if (isUsingSkeleton && communicationPrefetch.key === toastPrefetchKey) {
      for (const id of communicationCarouselIdsForPrefetch) {
        if (!communicationPrefetch.availableIds.has(id)) unavailable.add(id);
      }
    }
    return unavailable;
  }, [
    collapsedCommunicationIds,
    communicationPrefetch,
    communicationCarouselIdsForPrefetch,
    isUsingSkeleton,
    toastPrefetchKey,
  ]);

  /** Suppress hero auto focus only when the top toast is available to receive focus. */
  const topToastFocusCarouselId = useMemo(() => {
    if (!isHomeLander || !carouselsToShow?.size) return '';
    for (let i = 0; i < carouselsToShow.size; i += 1) {
      const c = carouselsToShow.get(i);
      const rowType = readCarouselRowType(c);
      if (!isToastBannerRowType(rowType)) continue;
      const enabled = isCommunicationBannerFetchEnabledForRowType(
        rowType,
        toastActiveForCommunication,
        rowAdsActiveForCommunication,
      );
      if (!enabled) return '';
      const id = String(getCarouselRowBannerId(c) ?? '').trim();
      if (!id || unavailableCommunicationIds.has(id)) return '';
      return id;
    }
    return '';
  }, [
    isHomeLander,
    carouselsToShow,
    toastActiveForCommunication,
    rowAdsActiveForCommunication,
    unavailableCommunicationIds,
  ]);

  useEffect(() => {
    if (!isUsingSkeleton || isFetching) return undefined;
    const idsForPrefetch = toastPrefetchKey.split('|').filter(Boolean);
    if (
      idsForPrefetch.length === 0
      || communicationPrefetch.key === toastPrefetchKey
    ) return undefined;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(idsForPrefetch.map(async (carouselId) => {
        try {
          const raw = await getCommunicationCarousel({ carouselId });
          return [carouselId, Boolean(mapHomeBannerPayload(raw))];
        } catch {
          return [carouselId, false];
        }
      }));
      if (!cancelled) {
        setCommunicationPrefetch({
          key: toastPrefetchKey,
          availableIds: new Set(results.filter(([, available]) => available).map(([id]) => id)),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isUsingSkeleton, isFetching, toastPrefetchKey, communicationPrefetch.key]);

  const showLanderSkeleton = (isUsingSkeleton && isFetching)
    || (
      isUsingSkeleton
      && !isFetching
      && communicationCarouselIdsForPrefetch.length > 0
      && communicationPrefetch.key !== toastPrefetchKey
    );

  const isRecentlyWatchedInCarousels = useMemo(() => !!carousels?.find(
    (carousel) => carousel?.sourceCarouselId === SOURCE_CAROUSEL_ID.RECENTLY_WATCHED,
  ), [carousels]);

  const initialSectionIndex = useMemo(
    () => (wasCarouselFocused || initialSectionIndexEpg || parentalBlockedChannelId ? undefined : Number.parseFloat(sessionStorage.getItem('initialSectionIndex')) || 0),
    [initialSectionIndexEpg, parentalBlockedChannelId, wasCarouselFocused],
  );

  const updatePositionCarousel = useCallback((carouselIndex, newCarouselPosition) => {
    getPositionsList()[carouselIndex] = newCarouselPosition;
  }, [getPositionsList]);

  const updateTranslate = useCallback((carouselIndex, newCarouselPosition) => {
    getTranslateValuesList()[carouselIndex] = newCarouselPosition;
  }, [getTranslateValuesList]);

  const handleCarouselFetchComplete = useCallback((carouselId, carousel) => {
    setFetchedCarouselsIds((current) => current.add(carouselId));
    setFetchedCarousels((current) => current.push(carousel));
  }, []);

  const updateWasCarouselFocused = useCallback(() => {
    setWasCarouselFocused(true);
  }, []);

  const getCardTypeByIndex = useCallback((index) => {
    const carouselInfo = carouselsToShow?.get(index);

    // It should be removed from the code when pages v4 is the final version.
    const entityType = isCurableBannerActive
      ? EntityName.BANNER
      : BannerType.UPSELL_BANNER;

    if (readCarouselField(carouselInfo, 'type') === entityType) return CardType.UPSELL_BANNER;

    if (readCarouselField(carouselInfo, 'type') === SOURCE_CAROUSEL_ID.ROW_ADS) return CardType.EMPTY;

    const disabledByFlag = cardTypeWhenCommunicationRowDisabledByFlag(carouselInfo, {
      toastActive: toastActiveForCommunication,
      landerActive: landerBannerActiveForCommunication,
      rowAdsActive: rowAdsActiveForCommunication,
    });
    if (disabledByFlag !== undefined) return disabledByFlag;

    const stackCtx = {
      carouselsToShow,
      firstToastBannerCarouselId,
      isToastBannerActive: toastActiveForCommunication,
      isRowAdsBannerActive: rowAdsActiveForCommunication,
      isLanderBannerActive: landerBannerActiveForCommunication,
    };
    const stackCardType = resolveStackCardTypeForIndex(index, stackCtx);
    if (stackCardType !== undefined) {
      return adjustStackCardTypeForCollapsedCommunication({
        index,
        stackCardType,
        carouselsToShow,
        collapsedIds: unavailableCommunicationIds,
        noFeatured: withoutFeatured,
      });
    }

    const matchId = readCarouselField(carouselInfo, 'sourceCarouselId')
      || readCarouselField(carouselInfo, 'elementId');
    const carouselFromStoreById = matchId
      ? carouselsFromStore?.find(
        (c) => (c?.sourceCarouselId || c?.elementId) === matchId,
      )
      : undefined;
    const carouselFromStore = carouselFromStoreById ?? (
      showUpSellBannerCarousel
        ? carouselsFromStore?.get(index === 0 ? 0 : index - 1)
        : carouselsFromStore?.get(index)
    );

    const cardType = getCardType(carouselFromStore, withoutFeatured);
    const initialCardType = getInitialCardType(carouselInfo, withoutFeatured);

    return carouselFromStore ? cardType : initialCardType;
  }, [
    carouselsFromStore,
    carouselsToShow,
    withoutFeatured,
    showUpSellBannerCarousel,
    isCurableBannerActive,
    firstToastBannerCarouselId,
    toastActiveForCommunication,
    rowAdsActiveForCommunication,
    landerBannerActiveForCommunication,
    unavailableCommunicationIds,
  ]);

  const getItemSize = useCallback((index) => {
    const cardType = getCardTypeByIndex(index);
    return sizeByViewport(
      getCarouselHeight(cardType) ?? -1,
    );
  }, [getCardTypeByIndex]);

  const initialSectionIndexToUse = useMemo(() => {
    if (
      !carouselsToShow
      || (!initialSectionIndex && initialSectionIndex !== 0)
    ) return initialSectionIndex;

    let index = initialSectionIndex;
    while (getItemSize(index) === 0 && index < carouselsToShow?.size) index += 1;
    return index;
  }, [carouselsToShow, getItemSize, initialSectionIndex]);

  const itemData = useMemo(() => ({
    carouselsInfo: carouselsToShow,
    pageId,
    sourceProvider,
    scrolled,
    noFeatured: withoutFeatured,
    noTitle: withoutTitle,
    fetchedCarousels: fetchedCarouselsIds,
    getPositionsList,
    getTranslateValuesList,
    initialSectionIndex: initialSectionIndexToUse,
    goToSidebar,
    handleCarouselFetchComplete,
    updateWasCarouselFocused,
    wasCarouselFocused,
    updatePositionCarousel,
    updateTranslate,
    onTopCarouselFocusKeyChange,
    registerCommunicationUnavailable,
    topToastFocusCarouselId,
    unavailableCommunicationIds,
  }), [
    withoutFeatured,
    withoutTitle,
    carouselsToShow,
    pageId,
    sourceProvider,
    scrolled,
    fetchedCarouselsIds,
    getPositionsList,
    getTranslateValuesList,
    initialSectionIndexToUse,
    goToSidebar,
    handleCarouselFetchComplete,
    updatePositionCarousel,
    updateTranslate,
    updateWasCarouselFocused,
    wasCarouselFocused,
    onTopCarouselFocusKeyChange,
    registerCommunicationUnavailable,
    topToastFocusCarouselId,
    unavailableCommunicationIds,
  ]);

  useEffect(() => {
    setCollapsedCommunicationIds(new Set());
  }, [pageId]);

  /* `position` only ever goes false → true, so one set on the next frame is
     enough. This used to re-set it from a 1ms setInterval for up to 60s — every
     tick after the first was a no-op that woke the main thread ~250x/s on the
     TVs. */
  useLayoutEffect(() => {
    const selectedCarousel = fetchedCarousels.find((carousel) => carousel.normalId === sessionStorage.getItem('lastViewedCarousel'));
    if (!selectedCarousel || (selectedCarousel?.contents?.size ?? 0) > 0) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => setPosition(true));
    return () => cancelAnimationFrame(frame);
  }, [fetchedCarousels]);

  useEffect(() => {
    if (pageId !== sessionStorage.getItem(STARTED_PAGE_ID)) {
      setAllSessionStorage();
      setPosition(true);
      if (parentalBlockedChannelId) {
        sessionStorage.setItem('parentalBlockedChannelId', parentalBlockedChannelId);
        sessionStorage.setItem('initialSectionIndex', -1);
      }
    }
  }, [pageId, parentalBlockedChannelId, setAllSessionStorage]);

  useEffect(() => {
    hideLoading();
  }, [hideLoading]);

  const recentlyWatchedInFetchedCarousels = useCallback(() => {
    if (onFetchRecentlyWatched && fetchedCarouselsIds?.has(SOURCE_CAROUSEL_ID.RECENTLY_WATCHED)) {
      onFetchRecentlyWatched(true);
    }
  }, [fetchedCarouselsIds, onFetchRecentlyWatched]);

  useEffect(() => {
    if (onFetchRecentlyWatched && isRecentlyWatchedInCarousels) {
      onFetchRecentlyWatched(false);
    }
  }, [isRecentlyWatchedInCarousels, onFetchRecentlyWatched]);

  useEffect(() => {
    recentlyWatchedInFetchedCarousels();
  }, [recentlyWatchedInFetchedCarousels]);

  useEffect(() => {
    let timeout;

    if (setIsPageFetching) {
      if (isFetching) {
        setIsPageFetching(true);
      } else {
        timeout = setTimeout(() => {
          setIsPageFetching(false);
        }, 400);
      }
    }

    return () => clearTimeout(timeout);
  }, [isFetching, setIsPageFetching]);

  const handleScroll = useCallback((scrollOffset, maxScrollOffset) => {
    if (position && sessionStorage.getItem('lastViewedCarousel') && scrollOffset <= 0) {
      sessionStorage.removeItem('lastViewedCarousel');
    }

    sessionStorage.setItem('offsetFromStartedPage', scrollOffset);
    setScrolled(!!scrollOffset);
    if (onScroll) onScroll(scrollOffset, maxScrollOffset);
  }, [onScroll, position]);

  return (
    showLanderSkeleton
      ? (
        <LanderLayout>
          {preContent}
          <LanderSkeleton withoutFeatured={withoutFeatured} />
        </LanderLayout>
      )
      : (
        <LanderLayout className="dtv-html5-lander-page">
          {preContent}
          <ItemDataProvider itemData={itemData}>
            <VirtualizedList
              className={`dtv-html5-lander-page__list ${className}`}
              height={landerPageHeight}
              style={styleProp}
              itemCount={carouselsToShow?.size || 0}
              itemSize={getItemSize}
              onScroll={handleScroll}
              renderer={LanderRow}
              initialSectionIndex={initialSectionIndex}
              startedPageId={pageId}
              initialScrollPosition={initialScrollPosition}
              resetScrollPosition={position}
              initialMaxOffset={initialMaxOffset}
              showItems={showItems}
            >
              {children}
            </VirtualizedList>
          </ItemDataProvider>
        </LanderLayout>
      )
  );
}

LanderPage.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  pageId: PropTypes.string.isRequired,
  sourceProvider: PropTypes.string,
  children: PropTypes.node,
  preContent: PropTypes.node,
  onScroll: PropTypes.func,
  initialMaxOffset: PropTypes.number,
  withoutFeatured: PropTypes.bool,
  onFetchRecentlyWatched: PropTypes.func,
  withoutTitle: PropTypes.bool,
  setIsPageFetching: PropTypes.func,
  isUsingSkeleton: PropTypes.bool,
  carouselFilter: PropTypes.func,
  showItems: PropTypes.bool,
  onTopCarouselFocusKeyChange: PropTypes.func,
};

export default memo(LanderPage);
